import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'next-i18next';
import { EyeSlashIcon } from '@heroicons/react/24/solid';
import { RadioGroup as HeadlessRadioGroup } from '@headlessui/react';

import { Select, SelectOptionType, Button as ButtonV2, Heading, Modal, ModalContent } from '@restworld/ui-ds-base';
import { NPSFeedbackData } from '../hooks/use-put-offline-job-position';

import { cn } from '@restworld/utils-common';
interface PutOfflineJobPositionNPSModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: NPSFeedbackData) => void;
  isLoading?: boolean;
}

const NPS_SCORES = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10];

// Soglie NPS:
//   1-6  → mostra domanda "cosa non ha funzionato" (+ flow Recovery post-submit)
//   7-8  → mostra domanda "quando riattiveresti" (success modal standard)
//   9-10 → mostra domanda "quando riattiveresti" (+ flow Referral post-submit)
const NPS_MOTIVE_MAX = 6;
const NPS_REACTIVATION_MIN = 7;

const MOTIVE_OPTIONS = [
  { key: 'few_candidates', label: 'nps_motive_few_candidates', value: 'few_candidates' },
  { key: 'candidates_not_suitable', label: 'nps_motive_candidates_not_suitable', value: 'candidates_not_suitable' },
  { key: 'service_too_slow', label: 'nps_motive_service_too_slow', value: 'service_too_slow' },
  { key: 'too_expensive', label: 'nps_motive_too_expensive', value: 'too_expensive' },
  { key: 'platform_difficult', label: 'nps_motive_platform_difficult', value: 'platform_difficult' },
  { key: 'support_insufficient', label: 'nps_motive_support_insufficient', value: 'support_insufficient' },
  { key: 'hired_elsewhere', label: 'nps_motive_hired_elsewhere', value: 'hired_elsewhere' },
  { key: 'other', label: 'nps_motive_other', value: 'other' },
];

const REACTIVATION_OPTIONS = [
  { key: 'within_1_month', label: 'nps_reactivation_within_1_month', value: 'within_1_month' },
  { key: 'within_3_months', label: 'nps_reactivation_within_3_months', value: 'within_3_months' },
  { key: 'within_6_months', label: 'nps_reactivation_within_6_months', value: 'within_6_months' },
  { key: 'more_than_6_months', label: 'nps_reactivation_more_than_6_months', value: 'more_than_6_months' },
  { key: 'not_sure', label: 'nps_reactivation_not_sure', value: 'not_sure' },
];

export function PutOfflineJobPositionNPSModal({
  isOpen,
  onClose,
  onSubmit,
  isLoading,
}: PutOfflineJobPositionNPSModalProps) {
  const { t } = useTranslation('job-positions-page');

  const [npsScore, setNpsScore] = useState<string | null>(null);
  const [motiveForCancelling, setMotiveForCancelling] = useState<SelectOptionType | undefined>(undefined);
  const [reactivationDate, setReactivationDate] = useState<SelectOptionType | undefined>(undefined);
  const [additionalFeedbackText, setAdditionalFeedbackText] = useState<string>('');

  const showMotiveQuestion = npsScore !== null && Number(npsScore) <= NPS_MOTIVE_MAX;
  const showReactivationQuestion = npsScore !== null && Number(npsScore) >= NPS_REACTIVATION_MIN;

  // Reset answers when NPS score changes
  useEffect(() => {
    if (!showReactivationQuestion && reactivationDate) {
      setReactivationDate(undefined);
    }
    if (!showMotiveQuestion && motiveForCancelling) {
      setMotiveForCancelling(undefined);
    }
    // The additional feedback field only belongs to the detractor (≤ 6) branch
    if (!showMotiveQuestion && additionalFeedbackText) {
      setAdditionalFeedbackText('');
    }
  }, [showReactivationQuestion, showMotiveQuestion, reactivationDate, motiveForCancelling, additionalFeedbackText]);

  const handleSubmit = useCallback(() => {
    // Validazione: NPS obbligatorio + domanda condizionale in base al punteggio
    if (!npsScore) return;
    if (showMotiveQuestion && !motiveForCancelling?.value) return;

    if (showReactivationQuestion && !reactivationDate?.value) return;

    const trimmedFeedback = additionalFeedbackText.trim();

    onSubmit({
      npsScore: Number(npsScore),
      motiveForCancelling: showMotiveQuestion ? motiveForCancelling?.value : undefined,
      additionalFeedback: showMotiveQuestion && trimmedFeedback ? trimmedFeedback : undefined,
      reactivationDate: showReactivationQuestion ? reactivationDate?.value : undefined,
    });
  }, [
    npsScore,
    motiveForCancelling,
    additionalFeedbackText,
    reactivationDate,
    showMotiveQuestion,
    showReactivationQuestion,
    onSubmit,
  ]);

  const isFormValid =
    npsScore &&
    (!showMotiveQuestion || motiveForCancelling?.value) &&
    (!showReactivationQuestion || reactivationDate?.value);

  const handleClose = useCallback(() => {
    if (!isLoading) {
      setNpsScore(null);
      setMotiveForCancelling(undefined);
      setReactivationDate(undefined);
      setAdditionalFeedbackText('');
      onClose();
    }
  }, [isLoading, onClose]);

  const titleEl = (
    <Heading color="neutral" align="center" className="leading-[30px] px-4 sm:px-0">
      {t('nps_modal_title')}
    </Heading>
  );

  const footerEl = (
    <div className="flex flex-col justify-center items-center gap-4 w-full">
      <ButtonV2
        onClick={handleSubmit}
        fullwidth
        loading={isLoading}
        variant="primary"
        disabled={isLoading || !isFormValid}
      >
        {t('nps_submit_feedback_button')}
      </ButtonV2>
    </div>
  );

  return (
    <Modal open={isOpen} onOpenChange={(v) => !v && handleClose()}>
      <ModalContent className="w-full max-w-[640px] lg:max-w-screen-sm" title={titleEl} footer={footerEl}>
        <div className="flex flex-col gap-6 w-full mt-6">
          {/* NPS Score Question */}
          <div className="space-y-3">
            <label className="block text-base font-medium text-neutral-700 mb-2">
              {t('nps_question_recommend')}
              <span className="text-red-500 ml-1">*</span>
            </label>
            <div className="flex flex-col items-start w-full">
              <HeadlessRadioGroup
                value={npsScore}
                onChange={(newValue) => setNpsScore(newValue)}
                className="flex flex-row justify-between items-center gap-1 w-full"
              >
                {NPS_SCORES.map((score) => {
                  const scoreStr = score.toString();
                  return (
                    <HeadlessRadioGroup.Option key={score} value={scoreStr} className="flex-1">
                      {({ checked }) => (
                        <NPSButton score={score} checked={checked}>
                          {scoreStr}
                        </NPSButton>
                      )}
                    </HeadlessRadioGroup.Option>
                  );
                })}
              </HeadlessRadioGroup>
              {/* Legenda NPS */}
              <NPSLegend className="mt-3">
                <LegendItem>
                  <LegendLabel>1-6</LegendLabel>
                  <LegendText>→ {t('nps_legend_1_6')}</LegendText>
                </LegendItem>
                <LegendItem>
                  <LegendLabel>7-8</LegendLabel>
                  <LegendText>→ {t('nps_legend_7_8')}</LegendText>
                </LegendItem>
                <LegendItem>
                  <LegendLabel>9-10</LegendLabel>
                  <LegendText>→ {t('nps_legend_9_10')}</LegendText>
                </LegendItem>
              </NPSLegend>
            </div>
          </div>

          {/* Reactivation Date Question (conditional on NPS >= 7) */}
          {showReactivationQuestion && (
            <div className="space-y-3">
              <label htmlFor="reactivation" className="block text-base font-medium text-neutral-700 mb-2">
                {t('nps_question_when_need_staff_again')}
                <span className="text-red-500 ml-1">*</span>
              </label>
              <Select
                fullwidth
                options={REACTIVATION_OPTIONS.map((option) => ({ ...option, label: t(option.label) }))}
                placeholder={t('nps_select_option')}
                value={reactivationDate}
                onChange={(newValue) => setReactivationDate(newValue)}
              />
            </div>
          )}
        </div>

        {/* Motive for Cancelling Question - only if NPS <= 6 */}
        {showMotiveQuestion && (
          <div className="space-y-3">
            <label htmlFor="motive" className="block text-base font-medium text-neutral-700 mb-2">
              {t('nps_question_what_didnt_work')}
              <span className="text-red-500 ml-1">*</span>
            </label>
            <Select
              fullwidth
              options={MOTIVE_OPTIONS.map((option) => ({ ...option, label: t(option.label) }))}
              placeholder={t('nps_select_option')}
              value={motiveForCancelling}
              onChange={(newValue) => setMotiveForCancelling(newValue)}
            />
            {/* Optional free-text follow-up, always shown to detractors (NPS ≤ 6) */}
            <label htmlFor="additional-feedback" className="block text-base font-medium text-neutral-700 mt-4 mb-2">
              {t('nps_question_additional_feedback')}
            </label>
            <textarea
              id="additional-feedback"
              value={additionalFeedbackText}
              onChange={(e) => setAdditionalFeedbackText(e.target.value)}
              placeholder={t('nps_additional_feedback_placeholder')}
              rows={3}
              maxLength={500}
              className="w-full border border-neutral-90 rounded-lg bg-neutral-10 py-3 px-4 text-base font-medium text-neutral-700 resize-none transition duration-200 ease-in-out placeholder:text-base placeholder:font-normal placeholder:text-neutral-200 focus:outline-none focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
            />
          </div>
        )}
      </ModalContent>
    </Modal>
  );
}

// Componenti per la legenda NPS
const NPSLegend = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <div className={cn('w-full border border-neutral-40 rounded-lg p-2 flex flex-col gap-1', className)} {...props} />
);

const LegendItem = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <div className={cn('flex items-start gap-1 text-sm text-neutral-200', className)} {...props} />
);

const LegendLabel = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <span className={cn('font-bold whitespace-nowrap', className)} {...props} />
);

const LegendText = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <span className={cn('font-normal', className)} {...props} />
);

// Custom NPS Button component that replicates the Figma design
const NPSButton = ({
  score,
  checked,
  className,
  ...props
}: { score: number; checked: boolean } & React.HTMLAttributes<HTMLElement>) => {
  const colorClasses =
    score >= 9
      ? checked
        ? 'bg-green-700 text-neutral-10 border-green-700 hover:bg-green-700'
        : 'bg-green-50 text-green-700 border-green-700 hover:bg-green-100'
      : score >= 7
      ? checked
        ? 'bg-orange-700 text-neutral-10 border-orange-700 hover:bg-orange-700'
        : 'bg-orange-50 text-orange-700 border-orange-700 hover:bg-orange-100'
      : checked
      ? 'bg-red-700 text-neutral-10 border-red-700 hover:bg-red-700'
      : 'bg-red-50 text-red-700 border-red-700 hover:bg-red-100';

  return (
    <div
      className={cn(
        'flex items-center justify-center border border-solid rounded-lg font-medium text-base leading-normal cursor-pointer transition-colors h-12 px-3 w-full',
        colorClasses,
        className
      )}
      {...props}
    />
  );
};

export default PutOfflineJobPositionNPSModal;
